import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

/**
 * Iter-158.0 — Production detail.
 *
 * Lists Projects inside one Production. Owner can create additional
 * Projects. Each Project row shows its shared scene / character
 * counts and the departments already activated.
 */
export default function BetaProduction() {
    const { productionId } = useParams();
    const nav = useNavigate();
    const [state, setState] = useState({ production: null, projects: [] });
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    // Iter-158.6.A1 Phase A UAT fix P1.4 — free-text "Kind" removed
    // from the create form. `production_type` (feature_film / tv_single
    // / tv_series) is the canonical field, mirroring legacy
    // CreateProject.jsx. `kind` stays on the model for schema stability
    // but no longer surfaces in the UI.
    const [productionType, setProductionType] = useState("feature_film");
    const [creating, setCreating] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    // Iter-158.3 — Import Legacy H&M project state.
    const [importOpen, setImportOpen] = useState(false);
    const [legacyProjects, setLegacyProjects] = useState([]);
    const [legacyLoading, setLegacyLoading] = useState(false);
    const [selectedLegacyId, setSelectedLegacyId] = useState("");
    const [confirmName, setConfirmName] = useState("");
    const [importName, setImportName] = useState("");
    const [importing, setImporting] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/beta/productions/${productionId}`);
            setState({
                production: data?.production ?? null,
                projects: Array.isArray(data?.projects) ? data.projects : [],
            });
        } catch (e) {
            toast.error(formatApiError(e));
            if (e?.response?.status === 404) nav("/beta");
        } finally {
            setLoading(false);
        }
    }, [productionId, nav]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setCreating(true);
        try {
            const { data } = await api.post(`/beta/productions/${productionId}/projects`, {
                name: name.trim(),
                // Iter-158.6.A1 Phase A UAT fix P1.4 — canonical only,
                // no free-text `kind` sent.
                production_type: productionType,
            });
            setName(""); setProductionType("feature_film"); setShowCreate(false);
            // Iter-158.7 Route-2 Iteration 1A — return the creator to
            // the project-level department picker (`/beta/projects/:id`)
            // rather than assuming Hair & Makeup. Beta is multi-
            // department by design; the project-entry page is where
            // the producer decides which department to open first.
            nav(`/beta/projects/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCreating(false);
        }
    };

    // Iter-158.3 — Import legacy H&M project handlers.
    const openImport = async () => {
        setImportOpen(true);
        setSelectedLegacyId("");
        setConfirmName("");
        setImportName("");
        setLegacyLoading(true);
        try {
            const { data } = await api.get(`/beta/hm/legacy-projects`);
            setLegacyProjects(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLegacyLoading(false);
        }
    };

    const selectedLegacy = legacyProjects.find((p) => p.id === selectedLegacyId) || null;

    const submitImport = async (e) => {
        e.preventDefault();
        if (!selectedLegacy) return;
        setImporting(true);
        try {
            const { data } = await api.post(
                `/beta/hm/productions/${productionId}/clone-from-legacy`,
                {
                    legacy_project_id: selectedLegacy.id,
                    new_project_name: importName.trim() || `${selectedLegacy.name} (beta)`,
                    confirm_legacy_name: confirmName,
                },
            );
            setImportOpen(false);
            toast.success("Imported into beta");
            nav(`/beta/projects/${data.beta_project_id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setImporting(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-neutral-950 text-neutral-500 p-8 text-sm">Loading…</div>;
    }

    return (
        <div data-testid="beta-production" className="min-h-screen bg-neutral-950 text-neutral-100 px-4 py-6 sm:px-8 sm:py-10">
            <header className="max-w-3xl mx-auto mb-6">
                {/* Iter-158.13.2.13 — Route 2 back-navigation.
                    Front Door is /projects (MyProjects). The
                    legacy /beta landing (BetaHome — "MULTI-
                    DEPARTMENT BETA / Legacy app") remains mounted
                    for direct URL access but is no longer part of
                    the normal navigation journey. */}
                <Link
                    data-testid="beta-production-back-my-projects"
                    to="/projects"
                    className="tap text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                >
                    ← My projects
                </Link>
                <h1 data-testid="beta-production-name" className="text-2xl sm:text-3xl font-semibold mt-2">
                    {state.production?.name}
                </h1>
                <p className="text-[12px] text-neutral-500 mt-1">
                    {state.projects.length} {state.projects.length === 1 ? "project" : "projects"}
                </p>
            </header>

            <div className="max-w-3xl mx-auto space-y-3">
                {state.projects.length === 0 ? (
                    <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-6 text-center">
                        <p className="text-sm text-neutral-300">No projects yet.</p>
                        <p className="text-[12px] text-neutral-500 mt-1">Create a project to upload a canonical script and activate departments.</p>
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {state.projects.map((p) => (
                            <li key={p.id}>
                                <Link
                                    data-testid={`beta-project-row-${p.id}`}
                                    to={`/beta/projects/${p.id}`}
                                    className="tap block rounded-xl border border-neutral-800 bg-neutral-900/60 p-4 hover:bg-neutral-900"
                                >
                                    <div className="flex items-baseline justify-between gap-3">
                                        <p className="text-sm font-medium text-neutral-100">{p.name}</p>
                                        {p.kind ? (
                                            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{p.kind}</span>
                                        ) : null}
                                    </div>
                                    <p className="text-[12px] text-neutral-500 mt-1">
                                        {p.scene_count} scenes · {p.character_count} characters · {p.departments?.length ?? 0} dept{p.departments?.length === 1 ? "" : "s"}
                                    </p>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}

                {showCreate ? (
                    <form onSubmit={submit} className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4 space-y-3">
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-1.5">Project name</label>
                            <input
                                data-testid="beta-new-project-name"
                                autoFocus
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-neutral-600"
                                placeholder="e.g. Meantime — Episode 2"
                            />
                        </div>
                        {/* Iter-158.6.A1 UAT fix P1.5 — production_type radio.
                            Keys/labels mirror legacy CreateProject.jsx exactly
                            so the beta workspace uses the same terminology and
                            downstream EpisodicScriptSetup gate. Iter-158.6.A1
                            Phase A UAT fix P1.4 — free-text "Kind" input
                            removed (it duplicated production_type). */}
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-1.5">Production type</label>
                            <div className="grid grid-cols-1 gap-2">
                                {[
                                    { key: "feature_film", label: "Feature Film", hint: "One script. One unified scene list." },
                                    { key: "tv_single", label: "Single TV Episode", hint: "One script for a one-off TV piece or pilot." },
                                    { key: "tv_series", label: "Episodic TV Series", hint: "One script per episode — Scenes group by episode." },
                                ].map((opt) => (
                                    <label
                                        key={opt.key}
                                        data-testid={`beta-new-project-type-${opt.key}`}
                                        className={`cursor-pointer rounded-lg border px-3 py-2 text-sm ${productionType === opt.key ? "border-amber-500/60 bg-amber-500/10 text-amber-100" : "border-neutral-800 bg-neutral-950 text-neutral-200 hover:border-neutral-600"}`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <input
                                                type="radio"
                                                name="beta_production_type"
                                                value={opt.key}
                                                checked={productionType === opt.key}
                                                onChange={() => setProductionType(opt.key)}
                                                className="mt-1"
                                            />
                                            <div>
                                                <div className="font-semibold">{opt.label}</div>
                                                <div className="text-[11px] text-neutral-400 mt-0.5">{opt.hint}</div>
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="submit"
                                data-testid="beta-new-project-submit"
                                disabled={creating || !name.trim()}
                                className="tap px-4 h-9 rounded-full text-[12px] uppercase tracking-[0.2em] bg-amber-500/15 text-amber-200 border border-amber-500/30 hover:bg-amber-500/25 disabled:opacity-50"
                            >
                                {creating ? "Creating…" : "Create"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowCreate(false); setName(""); setProductionType("feature_film"); }}
                                className="tap px-4 h-9 rounded-full text-[12px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                ) : (
                    <button
                        type="button"
                        data-testid="beta-new-project-open"
                        onClick={() => setShowCreate(true)}
                        className="tap w-full rounded-xl border border-dashed border-neutral-700 hover:border-neutral-500 p-4 text-sm text-neutral-300"
                    >
                        + New Project
                    </button>
                )}

                {/* Iter-158.3 — Import Legacy Hair & Makeup project. Lives
                    inside a Production because the result is a new beta
                    Project inside it. Never touches the legacy source. */}
                <button
                    type="button"
                    data-testid="beta-import-legacy-hm-open"
                    onClick={openImport}
                    className="tap w-full rounded-xl border border-dashed border-amber-500/30 hover:border-amber-500/60 p-4 text-sm text-amber-200"
                >
                    Import Legacy Hair &amp; Makeup Project…
                </button>
            </div>

            {importOpen ? (
                <div
                    data-testid="beta-import-legacy-hm-dialog"
                    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4"
                    onClick={() => (importing ? null : setImportOpen(false))}
                >
                    <div
                        className="w-full max-w-lg rounded-xl border border-neutral-800 bg-neutral-950 p-5 space-y-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.24em] text-amber-300/80">Import into {state.production?.name}</p>
                            <h2 className="text-lg font-semibold text-neutral-100 mt-1">Import Legacy Hair &amp; Makeup Project</h2>
                            <p className="text-[12px] text-neutral-400 mt-1 leading-relaxed">
                                Creates a new independent beta Project inside this Production. The legacy source is not modified.
                                Iter-158.3 copies project metadata, canonical script, scenes and character names. Notes, flags,
                                images and looks will arrive in a later phase.
                            </p>
                        </div>

                        {legacyLoading ? (
                            <p className="text-sm text-neutral-500">Loading legacy projects…</p>
                        ) : legacyProjects.length === 0 ? (
                            <p className="text-sm text-neutral-400">No legacy projects owned by this account.</p>
                        ) : (
                            <form onSubmit={submitImport} className="space-y-3">
                                <div>
                                    <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-1.5">
                                        Legacy project
                                    </label>
                                    <select
                                        data-testid="beta-import-legacy-hm-picker"
                                        value={selectedLegacyId}
                                        onChange={(e) => setSelectedLegacyId(e.target.value)}
                                        className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-neutral-600"
                                        required
                                    >
                                        <option value="">Choose a legacy project…</option>
                                        {legacyProjects.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                                {p.kind ? ` · ${p.kind}` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedLegacy ? (
                                    <>
                                        <div>
                                            <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-1.5">
                                                New beta project name (optional)
                                            </label>
                                            <input
                                                data-testid="beta-import-legacy-hm-name"
                                                value={importName}
                                                onChange={(e) => setImportName(e.target.value)}
                                                className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-neutral-600"
                                                placeholder={`${selectedLegacy.name} (beta)`}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-1.5">
                                                Confirm — retype legacy project name to import
                                            </label>
                                            <input
                                                data-testid="beta-import-legacy-hm-confirm"
                                                value={confirmName}
                                                onChange={(e) => setConfirmName(e.target.value)}
                                                className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-neutral-600"
                                                placeholder={selectedLegacy.name}
                                                autoComplete="off"
                                                required
                                            />
                                            <p className="text-[11px] text-neutral-500 mt-1">
                                                We compare case-insensitively so exact capitalisation isn&rsquo;t required.
                                            </p>
                                        </div>
                                    </>
                                ) : null}

                                <div className="flex flex-wrap gap-2 pt-1">
                                    <button
                                        type="submit"
                                        data-testid="beta-import-legacy-hm-submit"
                                        disabled={importing || !selectedLegacy || !confirmName.trim()}
                                        className="tap px-4 h-9 rounded-full text-[12px] uppercase tracking-[0.2em] bg-amber-500/15 text-amber-200 border border-amber-500/30 hover:bg-amber-500/25 disabled:opacity-50"
                                    >
                                        {importing ? "Importing…" : "Import"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setImportOpen(false)}
                                        disabled={importing}
                                        className="tap px-4 h-9 rounded-full text-[12px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
